import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import useAuth from 'hooks/useAuth';
import { useState } from 'react';
import { useSelector } from 'store';
import { useFinishTimecardMutation, useLazyGetTimecardByIdQuery } from 'store/api/timecards';
import { TimecardListItemData } from 'types/timecards';
import { captureTimecardFinished, captureTimerStopped } from 'utils/timecardAnalytics';
import { useNavigateWithReturnState } from 'utils/useNavigateWithReturnState';
import { timecardDtoToForm, timecardUpdateRequest } from './timecardFormMapping';
import { usePauseTrackingTimecard } from './usePauseTrackingTimecard';
import { useTimecardFormContext } from './useTimecardFormContext';

/**
 * Finishes a draft, pausing its stopwatch first when it runs. The new design finishes the stored draft where the user
 * is; the classic design opens its Finish page, carrying the origin page (e.g. /timecards/draft with its filters) in
 * location state, so finishing returns to where the action started. `isFinishing` keeps a button disabled while the
 * finish runs.
 */
export const useOpenFinishTimecard = () => {
    const isNewDesign = useIsNewDesign();
    const navigate = useNavigateWithReturnState();
    const { user } = useAuth();
    const { trackedTimecardId, isStopwatchRunning } = useSelector((state) => state.timecard);
    const { pauseTracking } = usePauseTrackingTimecard();
    const { context, isLoading: isLoadingContext } = useTimecardFormContext();
    const [getTimecard] = useLazyGetTimecardByIdQuery();
    const [finishTimecard] = useFinishTimecardMutation();
    const [isFinishing, setIsFinishing] = useState(false);

    const isRunningHere = (timecardId: number) =>
        !!trackedTimecardId && parseInt(trackedTimecardId) === timecardId && isStopwatchRunning;

    const openFinish = async (timecardId: number) => {
        if (!isNewDesign) {
            if (isRunningHere(timecardId) && (await pauseTracking(timecardId))) {
                captureTimerStopped();
            }
            navigate(`/timecards/finish/${timecardId}`);
            return;
        }
        setIsFinishing(true);
        try {
            let stored = await getTimecard(timecardId).unwrap();
            // The finish stores the time it is sent, so finishing a running stopwatch, or past a failed pause, would
            // drop the running interval. The stored flag also sees a stopwatch started in another browser.
            if (stored.isStopwatchRunning) {
                const paused = await pauseTracking(timecardId);
                if (!paused) {
                    return;
                }
                captureTimerStopped();
                stored = paused;
            }
            await finishTimecard({
                ...timecardUpdateRequest(timecardDtoToForm(stored, context), stored),
                userId: user?.id,
                tenantId: user?.activeTenantId,
            }).unwrap();
            void captureTimecardFinished({
                timerLogs: stored.timerLogs,
                description: stored.description,
                timecardLanguageCode: stored.matter.timecardLanguageCode,
            });
        } catch {
            // The API layer toasts the failure.
        } finally {
            setIsFinishing(false);
        }
    };

    // A list's blockers were read before a stopwatch started from its row, which gives a draft without time something
    // to finish with.
    const finishBlockersOf = ({ id, finishBlockers }: Pick<TimecardListItemData, 'id' | 'finishBlockers'>) =>
        isRunningHere(id) ? finishBlockers.filter((blocker) => blocker !== 'NO_HOURS') : finishBlockers;

    return { openFinish, isFinishing: isFinishing || (isNewDesign && isLoadingContext), finishBlockersOf };
};
